import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,


  Skeleton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeSVG } from 'qrcode.react';
import CampaignService from '../../../../services/CampaignService';
import { Campaign } from '../../../../types/Campaign';

interface ApplicantManagementPanelProps {
  campaignId: string;
  organizationId: string;
}

/**
 * Panel for managing applicant access to the campaign application.
 * Allows setting application policy and viewing application links.
 */
const ApplicantManagementPanel: React.FC<ApplicantManagementPanelProps> = ({ campaignId, organizationId }) => {
  // State for application policy and campaign data

  const [campaignCode, setCampaignCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Application URL using campaign code
  const applicationUrl = `https://apply.mentoringfusion.com/${campaignCode}`;

  // Fetch campaign data to get campaign_code
  useEffect(() => {
    if (campaignId && organizationId) {
      setLoading(true);
      CampaignService.getCampaign(organizationId, campaignId)
        .then((campaign: Campaign) => {
          if (campaign && campaign.campaign_code) {
            setCampaignCode(campaign.campaign_code);
          } else {
            setError('Campaign code not available');
          }
        })
        .catch((err: Error) => {
          console.error('Error fetching campaign:', err);
          setError('Failed to load campaign data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [campaignId, organizationId]);


  const handleCopyLink = () => {
    navigator.clipboard.writeText(applicationUrl)
      .then(() => {
        // Could add a snackbar notification here
        console.log('Application link copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy link', err);
      });
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <Box sx={{ my: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="rectangular" width={80} height={80} sx={{ mr: 2 }} />
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}. Unable to generate application link and QR code.</Alert>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 2, width: 80, height: 80 }}>
              <QRCodeSVG value={applicationUrl} size={80} level="H" />
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Application Link
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {applicationUrl}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                Campaign Code: <strong>{campaignCode}</strong>
              </Typography>
              <Button
                startIcon={<ContentCopyIcon />}
                size="small"
                variant="outlined"
                onClick={handleCopyLink}
              >
                Copy Link
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ApplicantManagementPanel;
